import { api } from "@/lib/api";

export const getRobinhoodOverview = () =>
  api.get("/ecosystems/robinhood").then((r) => r.data);

export const getRobinhoodAssets = (params = {}) =>
  api.get("/ecosystems/robinhood/assets", { params }).then((r) => r.data);

export const getRobinhoodLeaderboard = (params = {}) =>
  api.get("/ecosystems/robinhood/leaderboard", { params }).then((r) => r.data);

export const getRobinhoodHype = (params = {}) =>
  api.get("/ecosystems/robinhood/hype", { params }).then((r) => r.data);

export const getRobinhoodCallers = (params = {}) =>
  api.get("/ecosystems/robinhood/callers", { params }).then((r) => r.data);

export const getRobinhoodMetrics = () =>
  api.get("/ecosystems/robinhood/metrics").then((r) => r.data);

export const getRobinhoodNetwork = () =>
  api.get("/ecosystems/robinhood/network").then((r) => r.data);

/** Staging/local demo stories (A/B/C). 404 on production. */
export const getRobinhoodDemoStories = () =>
  api.get("/ecosystems/robinhood/demo-stories").then((r) => r.data);

/**
 * Compact EVM address / wallet display: `0xde70…0001`.
 * Full value must remain available via title / aria-label / copy.
 */
export function shortContract(addr) {
  if (addr == null) return "";
  const s = String(addr);
  if (s.length < 12) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

/** Alias for caller wallets — same truncation contract as shortContract. */
export const shortWallet = shortContract;

export function explorerAddressUrl(contract) {
  if (!contract) return null;
  return `https://robinhoodchain.blockscout.com/address/${contract}`;
}

/** Normalize FastAPI / axios error payloads into a renderable string. */
export function formatRobinhoodApiError(err, fallback = "Failed to load Robinhood ecosystem") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item.msg === "string") return item.msg;
        try {
          return JSON.stringify(item);
        } catch {
          return null;
        }
      })
      .filter(Boolean);
    if (parts.length) return parts.join("; ");
  }
  if (detail != null && typeof detail === "object") {
    try {
      return JSON.stringify(detail);
    } catch {
      /* fall through */
    }
  }
  if (typeof err?.message === "string" && err.message.trim()) return err.message;
  return fallback;
}

/** Explicit empty-state copy for Robinhood surfaces (no fabricated placeholders). */
export const ROBINHOOD_EMPTY = {
  backing: "No verified Backing yet.",
  callers: "No caller activity yet.",
  outcomes: "Outcome history not yet available",
  scenario: "Scenario not ready.",
};

export const ROBINHOOD_PRIMARY_RANK_LABEL = "Ranked by verified Backing";

export const ROBINHOOD_STAGING_DISCLOSURE =
  "Staging demo — Backing and caller activity shown here includes controlled test interactions.";

export function isSourceBackedStockToken(a) {
  if (!a) return false;
  const at = a.asset_type;
  if (!["rwa", "tokenized_equity", "tokenized_etf"].includes(at)) return false;
  return (
    a.data_quality === "verified_source_backed" ||
    a.verification?.status === "source_backed" ||
    a.origin_platform === "robinhood_rhj"
  );
}

/** Earliest-call growth helper for caller cards (defensible when both values exist). */
export function backingGrowthSinceCall(earliest) {
  if (!earliest) return null;
  const at = earliest.backing_at_call_usd;
  const cur = earliest.current_backing_usd;
  if (at == null || cur == null) return null;
  const growth = Number(cur) - Number(at);
  if (Number.isNaN(growth)) return null;
  return { at: Number(at), current: Number(cur), growth };
}
